<template>
  <q-page class="flex flex-center">
    <q-card class="login-card">
      <q-card-section class="text-center">
        <div class="text-h5">{{ $t("auth.login") }}</div>
        <p class="text-grey-6">{{ $t("auth.welcomeMessage") }}</p>
      </q-card-section>

      <q-card-section>
        <q-form @submit.prevent="loginUser" class="q-gutter-md">
          <q-input
            v-model="credentials.email"
            :label="$t('auth.email')"
            type="email"
            outlined
            :rules="[
              (val) => !!val || $t('auth.emailRequired'),
              (val) => isValidEmail(val) || $t('auth.emailInvalid')
            ]"
            :error="emailError"
            stack-label
          >
            <template v-slot:prepend>
              <q-icon name="email" />
            </template>
          </q-input>
          <div v-if="emailError" class="text-negative text-caption">
            {{ emailError }}
          </div>

          <q-input
            v-model="credentials.password"
            :label="$t('auth.password')"
            :type="isPwd ? 'password' : 'text'"
            outlined
            :rules="[val => !!val || $t('auth.passwordRequired')]"
            :error="passwordError"
            stack-label
          >
            <template v-slot:prepend>
              <q-icon name="lock" />
            </template>
            <template v-slot:append>
              <q-icon
                :name="isPwd ? 'visibility_off' : 'visibility'"
                class="cursor-pointer"
                @click="isPwd = !isPwd"
              />
            </template>
          </q-input>
          <div v-if="passwordError" class="text-negative text-caption">
            {{ passwordError }}
          </div>

          <div class="row items-center justify-between q-mt-md">
            <q-checkbox v-model="rememberMe" :label="$t('auth.rememberMe')" />
            <router-link to="/forgot-password" class="text-primary">
              {{ $t("auth.forgotPassword") }}
            </router-link>
          </div>

          <q-btn
            type="submit"
            color="primary"
            class="full-width q-mt-lg"
            :label="$t('auth.login')"
            :loading="loading"
          />
        </q-form>
      </q-card-section>

     <!-- <q-table
      v-if="authStore.users && Array.isArray(authStore.users) && authStore.users.length > 0"
      :rows="authStore.users"
      :columns="userColumns"
      row-key="id"
     />
     <p v-else>{{ $t("dashboard.noUsersFound") }}</p> -->

      <q-card-section class="text-center q-pa-none">
        <p class="text-grey-6">
          {{ $t("auth.noAccount") }}
          <router-link to="/register" class="text-primary">
            {{ $t("auth.register") }}
          </router-link>
        </p>
        <p>
          <router-link to="/home" class="text-secondary">
            {{ $t("auth.purchaseApp") }}
          </router-link>
        </p>
      </q-card-section>
    </q-card>
  </q-page>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "../stores/auth";
import { useQuasar } from "quasar";
import { useI18n } from "vue-i18n";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../services/firebase";

const { t } = useI18n();
const router = useRouter();
const authStore = useAuthStore();
const $q = useQuasar();
// const users = ref([]);

// const fetchUsers = async () => {
//   try {
//     const querySnapshot = await getDocs(collection(db, "users")); // Fetch documents from 'users' collection
//     users.value = querySnapshot.docs.map(doc => ({
//       id: doc.id,
//       ...doc.data()
//     })); // Map documents to user objects
//   } catch (error) {
//     console.error("Error fetching users:", error);
//     users.value = [];
//   }
// };

const credentials = ref({ email: "", password: "" });
const isPwd = ref(true);
const rememberMe = ref(false);
const loading = ref(false);
const emailError = ref(null);
const passwordError = ref(null);

const isValidEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

const loginUser = async () => {
  emailError.value = null;
  passwordError.value = null;

  try {
    loading.value = true;

    if (!credentials.value.email || !isValidEmail(credentials.value.email)) {
      emailError.value = t("auth.enterValidEmail");
      loading.value = false;
      return;
    }
    if (!credentials.value.password) {
      passwordError.value = t("auth.passwordRequired");
      loading.value = false;
      return;
    }

    const userCredential = await authStore.login(credentials.value.email, credentials.value.password);
    
    if (!userCredential?.user?.uid) {
      throw new Error("Invalid login response from Firebase.");
    }

    await authStore.fetchUserRole(userCredential.user.uid);

    $q.notify({ type: "positive", message: t("auth.loginSuccess") });
    router.push("/dashboard");
  } catch (error) {
    console.error("Login Error:", error);
    $q.notify({
      type: "negative",
      message: error.message || t("auth.loginFailed"),
    });
  } finally {
    loading.value = false;
  }
};

onMounted(async () => {
  await authStore.fetchUsers();
});
</script>

<style scoped>
.login-card {
  max-width: 400px;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
}

.login-card .text-h5 {
  font-weight: bold;
}

.text-negative {
  margin-top: 5px;
}
</style>